import type {
  FixPlanReportOptions,
  AttributionReportIncludeOptions as ReportOptions,
} from '../../../../../shared/reports';
import type { FixPlanReportOptionDetail, ReportCategory, ReportFormatType, ReportOptionDetail } from '../shared/types';

/**
 * Constants
 */

// Categories
export const ReportCategories = {
  Licensing: 'licensing',
  Vulnerability: 'vulnerability',
  SBOM: 'sbom',
  RemediationGuidance: 'remediation-guidance',
} as const;

export const ReportCategoryTitles: Record<ReportCategory, string> = {
  licensing: 'Licensing',
  vulnerability: 'Vulnerability',
  sbom: 'SBOM',
  'remediation-guidance': 'Remediation Guidance',
} as const;

// Formats
export const ReportFormats = {
  // Licensing
  HTML: 'HTML',
  MD: 'MD',
  CSV: 'CSV',
  TXT: 'TXT',
  // Vulnerability + Licensing
  PDF: 'PDF',
  // SBOM
  SPDX: 'SPDX',
  SPDX_JSON: 'SPDX_JSON',
  SPDX3_JSON: 'SPDX3_JSON',
  CYCLONEDX_JSON: 'CYCLONEDX_JSON',
  CYCLONEDX_XML: 'CYCLONEDX_XML',
  // Fix Plans
  JSON: 'JSON',
} as const;

export const ReportOptionDetails: Record<keyof ReportOptions, ReportOptionDetail> = {
  /** @deprecated No backend format reads sections.concludedLicenses. Use dependencyData.concludedLicenses instead. */
  includeConcludedLicenses: {
    key: 'includeConcludedLicenses',
    title: 'Concluded Licenses',
    description: `Licenses you've declared as governing the project`,
    category: [ReportCategories.Licensing],
    format: new Set<ReportFormatType>(),
  },
  includeProjectLicense: {
    key: 'includeProjectLicense',
    title: 'Project Declared License',
    description: 'The license of the project analyzed by FOSSA',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set([
      ReportFormats.HTML, ReportFormats.PDF, ReportFormats.MD, ReportFormats.TXT, ReportFormats.CSV,
      ReportFormats.SPDX, ReportFormats.SPDX_JSON, ReportFormats.SPDX3_JSON, ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML,
    ]),
  },
  includeLicenseScan: {
    key: 'includeLicenseScan',
    title: 'First Party Licenses',
    description: 'Licenses found within your code.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set([
      ReportFormats.HTML, ReportFormats.PDF, ReportFormats.MD, ReportFormats.TXT, ReportFormats.CSV,
      ReportFormats.SPDX, ReportFormats.SPDX_JSON, ReportFormats.SPDX3_JSON, ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML,
    ]),
  },
  includeDependencySummary: {
    key: 'includeDependencySummary',
    title: 'Dependency Summary',
    description: 'Overview of all packages included in your project and their license information.',
    category: [ReportCategories.Licensing],
    format: new Set([ReportFormats.HTML, ReportFormats.PDF, ReportFormats.MD, ReportFormats.TXT]),
  },
  includeDirectDependencies: {
    key: 'includeDirectDependencies',
    title: 'Direct Dependencies',
    description: 'Packages that your project explicitly depends on.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set([
      ReportFormats.HTML, ReportFormats.PDF, ReportFormats.MD, ReportFormats.TXT, ReportFormats.CSV,
      ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML,
    ]),
  },
  includeDeepDependencies: {
    key: 'includeDeepDependencies',
    title: 'Transitive Dependencies',
    description: 'Packages indirectly included through other dependencies.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set([
      ReportFormats.HTML, ReportFormats.PDF, ReportFormats.MD, ReportFormats.TXT,
      ReportFormats.CSV, ReportFormats.SPDX, ReportFormats.SPDX_JSON, ReportFormats.SPDX3_JSON,
      ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML,
    ]),
  },
  includeSnippetDependencies: {
    key: 'includeSnippetDependencies',
    title: 'Snippet Dependencies',
    description: 'Code fragments identified through snippet scanning that indicate use of external dependencies.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set([
      ReportFormats.HTML, ReportFormats.PDF, ReportFormats.MD, ReportFormats.TXT, ReportFormats.CSV,
      ReportFormats.SPDX, ReportFormats.SPDX_JSON, ReportFormats.SPDX3_JSON, ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML,
    ]),
  },
  /**
   * Exclude-style option: the backend param excludes unknown deps when set, and its
   * absence keeps them in the report. The V3 options panel renders it as an
   * include-style "Unknown Dependencies" checkbox and inverts at the mapping layer.
   */
  excludeUnknownDependencies: {
    key: 'excludeUnknownDependencies',
    title: 'Unknown Dependencies',
    description: 'Dependencies FOSSA could not identify or analyze. Included in reports by default.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set(Object.values(ReportFormats)),
  },
  /**
   * Exclude-style option rendered as its own checkbox under the V3 Exclude Package Labels select,
   * not as an Included checkbox.
   */
  excludeLabeledDependencies: {
    key: 'excludeLabeledDependencies',
    title: 'Exclude Labeled Dependencies',
    description: 'Leave out every dependency that has a package label, so the report lists only unlabeled dependencies.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set(Object.values(ReportFormats)),
  },
  includeFOSSADependencies: {
    key: 'includeFOSSADependencies',
    title: 'FOSSA Projects',
    description: 'Include FOSSA Dependencies in Release Group Reports.',
    category: [ReportCategories.SBOM],
    format: new Set([ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML]),
  },
  /** @deprecated No backend format reads sections.licenseList. License hydration is triggered by dependencyData options. */
  includeLicenseList: {
    key: 'includeLicenseList',
    title: 'Full License List',
    description: 'Complete list of all licenses found across your project\'s dependencies.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set<ReportFormatType>(),
  },
  includeFileMatches: {
    key: 'includeFileMatches',
    title: 'License File Matches',
    description:
      'File paths within each dependency where license matches were detected.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set([ReportFormats.TXT, ReportFormats.CSV, ReportFormats.SPDX, ReportFormats.SPDX_JSON, ReportFormats.SPDX3_JSON]),
  },
  includeOpenVulnerabilities: {
    key: 'includeOpenVulnerabilities',
    title: 'Open Vulnerabilities',
    description:
      'Known security vulnerabilities that have not yet been resolved or ignored.',
    category: [ReportCategories.SBOM],
    format: new Set([ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML]),
  },
  includeClosedVulnerabilities: {
    key: 'includeClosedVulnerabilities',
    title: 'Closed Vulnerabilities',
    description:
      'Security vulnerabilities that have been resolved or ignored, including their resolution status.',
    category: [ReportCategories.SBOM],
    format: new Set([ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML]),
  },
  shouldUseCycloneDXVulnAnalysisState: {
    key: 'shouldUseCycloneDXVulnAnalysisState',
    title: 'CycloneDX Vuln Analysis State',
    description:
      'Report vulnerability analysis states and justifications using only the values defined by the CycloneDX specification, '
      + 'rather than FOSSA\'s own status names.',
    category: [ReportCategories.SBOM],
    format: new Set([ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML]),
  },
  includeSupportStatus: {
    key: 'includeSupportStatus',
    title: 'Support Status',
    description:
      'Whether each dependency is actively maintained or has been identified as abandoned.',
    category: [ReportCategories.SBOM],
    format: new Set([ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML]),
  },
  /** @deprecated Redundant with dependencyData.licenseHeaders, controlled via the CustomTextLicense dep-metadata option. */
  includeLicenseHeaders: {
    key: 'includeLicenseHeaders',
    title: 'License Headers',
    description: 'License information found in the header of a source file.',
    category: [ReportCategories.Licensing],
    format: new Set<ReportFormatType>(),
  },
  /** @deprecated No backend format reads sections.copyrightList. */
  includeCopyrightList: {
    key: 'includeCopyrightList',
    title: 'Copyrights',
    description: 'Ownership statements found in source files or license documents.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set<ReportFormatType>(),
  },
  includeNonLicenseCopyrights: {
    key: 'includeNonLicenseCopyrights',
    title: 'Copyrights from non-license text',
    description:
      'Copyright statements extracted from source code or documentation outside of license files.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set([ReportFormats.HTML, ReportFormats.PDF, ReportFormats.SPDX, ReportFormats.SPDX_JSON, ReportFormats.SPDX3_JSON]),
  },
  includeNoticeFiles: {
    key: 'includeNoticeFiles',
    title: 'Notice Files',
    description: 'Files that summarize license and copyright notices for a package.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set([
      ReportFormats.HTML,
      ReportFormats.PDF,
      ReportFormats.MD,
      ReportFormats.TXT,
      ReportFormats.SPDX,
      ReportFormats.SPDX_JSON,
      ReportFormats.SPDX3_JSON,
      ReportFormats.CYCLONEDX_JSON,
      ReportFormats.CYCLONEDX_XML,
    ]),
  },
  includeHashAndVersionData: {
    key: 'includeHashAndVersionData',
    title: 'Hash and Version Data',
    description: 'Unique identifiers and version details used to verify the exact package instance.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set([
      ReportFormats.HTML, ReportFormats.PDF, ReportFormats.MD, ReportFormats.TXT,
      ReportFormats.CSV, ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML,
    ]),
  },
  includePackageLabels: {
    key: 'includePackageLabels',
    title: 'Package Labels',
    description: 'Tags applied to packages to group, track, or filter dependencies.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: new Set([
      ReportFormats.HTML, ReportFormats.PDF, ReportFormats.MD, ReportFormats.TXT, ReportFormats.CSV,
      ReportFormats.SPDX, ReportFormats.SPDX_JSON, ReportFormats.SPDX3_JSON, ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML,
    ]),
  },
};

export const FixPlanReportOptionDetails: Record<keyof FixPlanReportOptions, FixPlanReportOptionDetail> = {
  includeQuickWins: {
    key: 'includeQuickWins',
    title: 'Quick Wins',
    alternativeTitle: 'Fix Quickly',
    description: 'Non-breaking changes that reduce the highest number of vulnerabilities with minimal effort.',
  },
  includeHighPriority: {
    key: 'includeHighPriority',
    title: 'High Priority Fixes',
    alternativeTitle: 'Prioritize Now',
    description: 'High-impact upgrades prioritized by risk signals such as exploitability and EPSS score.',
  },
  includeLowPriority: {
    key: 'includeLowPriority',
    title: 'Low Priority Fixes',
    alternativeTitle: 'Prioritize Next',
    description: 'Lower-risk upgrades identified by scoring signals such as exploitability and EPSS score.',
  },
  includeOutdatedDependencies: {
    key: 'includeOutdatedDependencies',
    title: 'Outdated Dependencies',
    alternativeTitle: 'Address Proactively',
    description: 'Dependencies that are multiple minor or major versions behind the latest release.',
  },
  includeMalware: {
    key: 'includeMalware',
    title: 'Malicious Dependencies',
    alternativeTitle: 'Malicious Dependencies',
    description: 'Dependencies identified as malicious and recommended for immediate removal.',
  },
  deduplicateOutdatedDeps: {
    key: 'deduplicateOutdatedDeps',
    title: 'Exclude Vulnerable',
    alternativeTitle: 'Exclude Vulnerable',
    description: 'Exclude fixes that do not address known vulnerabilities from the remediation plan.',
    isNested: true,
  },
  includeTransitiveVulns: {
    key: 'includeTransitiveVulns',
    title: 'Transitive Vulnerabilities',
    alternativeTitle: 'Transitive Vulnerabilities',
    description: 'Vulnerabilities introduced by indirect (transitive) dependencies rather than direct ones.',
  },
};
